import { BackgroundTypes, MapModes, ReactMap } from '@innovyze/inno-map';
import {
	useAuth0,
	useGetMapBackground,
	useIsFeatureEnabled,
} from '@innovyze/stylovyze';

import { MapStatusType } from '@Types';
import React from 'react';
import { useReactMapConfig } from '@innovyze/stylovyze';
import { useSelectMapInsertAsset } from '@Selectors';
import { useFeatureFlagBridge } from '@Utils';

export interface Props {
	logLevel?: MapStatusType;
}

const MapInsertAsset: React.FC<Props> = ({
	logLevel = MapStatusType.WARN,
}: Props) => {
	const isTilesV3Enabled = !!useIsFeatureEnabled('info-360-am-map-tiles-v-3');
	const isCloudOsTilesetEnabled = !!useIsFeatureEnabled(
		'i-360-tileset-read-from-cloudos',
	);

	const { token } = useAuth0();
	const featureFlagBridge = useFeatureFlagBridge();
	const stylovyzeDefaultBackground = useGetMapBackground() as BackgroundTypes;

	const inputs = useSelectMapInsertAsset(
		token,
		isTilesV3Enabled,
		isCloudOsTilesetEnabled,
	);

	const background = stylovyzeDefaultBackground ?? BackgroundTypes.OSMStreets;

	const reactMapConfig = useReactMapConfig();

	// Re-mount ReactMap when the selected asset changes. Without this key the
	// existing MapView is reused and keeps its previous center/zoom because
	// the position prop is only applied in the MapView ctor; subsequent prop
	// changes are picked up by a goTo effect that only fires when the deps
	// genuinely differ, which is unreliable across rapid asset selections.
	// Keying by asset id forces a fresh MapView per asset.
	const insetKey =
		inputs.selected && inputs.selected.length > 0
			? inputs.selected.map(s => `${s.layerId ?? ''}:${s.id}`).join('|')
			: 'no-selection';

	return (
		<ReactMap
			key={insetKey}
			featureFlagBridge={featureFlagBridge}
			config={reactMapConfig}
			mode={MapModes.Navigation}
			dataServices={inputs.sources}
			layerConfig={inputs.layers}
			autoResize={false}
			position={inputs.location?.position ?? undefined}
			selectedAssets={inputs.selected}
			showMinZoomWarning={false}
			logLevel={logLevel ?? MapStatusType.WARN}
			background={background}
		/>
	);
};

export default MapInsertAsset;
